import React, { useState, useEffect } from 'react';
import { Clock, Trash2, Plus, Check, X, AlertTriangle } from 'lucide-react';
import { getLaneTypeColor } from './colorUtils';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Sunday' },
  { value: 2, label: 'Monday' },
  { value: 3, label: 'Tuesday' },
  { value: 4, label: 'Wednesday' },
  { value: 5, label: 'Thursday' },
  { value: 6, label: 'Friday' },
  { value: 7, label: 'Saturday' }
];

interface MobileCardsProps {
  operatingHours: any[];
  laneTypes: any[];
  checkpoints: any[];
  terminals: any[];
  selectedDays: number[];
  onUpdate: (hourId: number, startTime: string, endTime: string) => Promise<void>;
  onDelete: (hourId: number) => void;
  onAdd: (context: { checkpoint_id: number; checkpoint_name: string; day_of_week: number; lanetype_id: number }) => void;
  canUpdate?: boolean;
}

export const MobileCards: React.FC<MobileCardsProps> = ({
  operatingHours,
  laneTypes,
  checkpoints,
  terminals,
  selectedDays,
  onUpdate,
  onDelete,
  onAdd,
  canUpdate = false
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasOverlap, setHasOverlap] = useState(false);

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  // Check for overlaps when editing
  useEffect(() => {
    if (editingId && editStartTime && editEndTime) {
      checkOverlap(editingId, editStartTime, editEndTime);
    } else {
      setHasOverlap(false);
    }
  }, [editStartTime, editEndTime, editingId, operatingHours]);

  const checkOverlap = (currentId: number, start: string, end: string) => {
    if (start >= end) {
      setHasOverlap(true);
      return;
    }

    // Find the current hour being edited to get its lane type and day
    const currentHour = operatingHours.find(h => h.lane_operating_hours_id === currentId);
    if (!currentHour) {
      setHasOverlap(false);
      return;
    }

    // Check against other slots for the same lane type and day
    const otherSlots = operatingHours.filter(h => 
      h.lane_operating_hours_id !== currentId &&
      h.lanetype.lanetype_id === currentHour.lanetype.lanetype_id &&
      h.day_of_week === currentHour.day_of_week
    );
    
    const overlaps = otherSlots.some(slot => {
      const slotStart = formatTime(slot.start_time);
      const slotEnd = formatTime(slot.end_time);
      
      // Check if time ranges overlap
      return (
        (start >= slotStart && start < slotEnd) ||
        (end > slotStart && end <= slotEnd) ||
        (start <= slotStart && end >= slotEnd)
      );
    });

    setHasOverlap(overlaps);
  };

  const handleStartEdit = (hour: any) => {
    setEditingId(hour.lane_operating_hours_id);
    setEditStartTime(formatTime(hour.start_time));
    setEditEndTime(formatTime(hour.end_time));
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditStartTime('');
    setEditEndTime('');
  };

  const handleSaveEdit = async (hourId: number) => {
    // Client-side validation
    if (!editStartTime || !editEndTime) {
      alert('Please enter both start and end times');
      return;
    }

    if (editStartTime >= editEndTime) {
      alert('End time must be after start time');
      return;
    }

    if (hasOverlap) {
      alert('Time slot overlaps with existing hours for this lane type');
      return;
    }

    setSaving(true);
    try {
      await onUpdate(hourId, `${editStartTime}:00`, `${editEndTime}:00`);
      setEditingId(null);
      setEditStartTime('');
      setEditEndTime('');
    } catch (error) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  // Single day view
  const selectedDay = selectedDays[0] || 2; // Default to Monday
  const dayInfo = DAYS_OF_WEEK.find(d => d.value === selectedDay);

  if (checkpoints.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <div className="text-gray-400 text-sm">
          Please select a terminal and checkpoint to view operating hours.
        </div>
      </div>
    );
  }

  // Group by checkpoint
  return (
    <div className="space-y-4">
      {checkpoints.map(checkpoint => {
        const terminal = terminals.find(t => t.terminal_id === checkpoint.terminal_id);
        
        // Get lane types for this checkpoint
        const checkpointLaneTypes = laneTypes.filter(lt => lt.checkpoint_id === checkpoint.checkpoint_id);
        
        return (
          <div key={checkpoint.checkpoint_id} className="space-y-3">
            {/* Checkpoint Header */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-400">
                {terminal?.terminal_name || 'Terminal'} › {checkpoint.checkpoint_name}
              </div>
              <div className="text-xl font-semibold text-white mt-1">
                📅 {dayInfo?.label}
              </div>
            </div>

            {/* Lane type cards for this checkpoint */}
            {checkpointLaneTypes.map(laneType => {
              const laneTypeColor = getLaneTypeColor(laneType.type_name);
              
              // Get hours for this lane type and day
              const hours = operatingHours.filter(h => 
                h.lanetype.lanetype_id === laneType.lane_type_id &&
                h.day_of_week === selectedDay
              );
              
              return (
                <div key={laneType.lane_type_id} className="bg-gray-800 rounded-lg p-4">
                  {/* Lane type header */}
                  <div className="mb-3 pb-3 border-b border-gray-700">
                    <div className="text-lg font-semibold text-white">{laneType.type_name}</div>
                  </div>

                  {/* Time slots or empty state */}
                  <div className="space-y-3">
                    {hours.length === 0 ? (
                      // Empty state - no slots
                      <div className="text-center py-6">
                        <div className="text-gray-500 text-sm mb-3">No operating hours set</div>
                        {canUpdate && (
                          <button
                            onClick={() => onAdd({
                              checkpoint_id: checkpoint.checkpoint_id,
                              checkpoint_name: checkpoint.checkpoint_name,
                              day_of_week: selectedDay,
                              lanetype_id: laneType.lane_type_id
                            })}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                          >
                            <Plus size={16} />
                            Add Hours
                          </button>
                        )}
                      </div>
                    ) : (
                      // Render existing time slots
                      hours.map((hour) => {
                        const isEditing = editingId === hour.lane_operating_hours_id;
                        
                        if (isEditing) {
                          return (
                            <div 
                              key={hour.lane_operating_hours_id} 
                              className={`${laneTypeColor.bg} border ${laneTypeColor.border} rounded-lg p-4`}
                            >
                              <div className="flex items-center gap-2 mb-3">
                                <input
                                  type="time"
                                  value={editStartTime}
                                  onChange={(e) => setEditStartTime(e.target.value)}
                                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                                  disabled={saving}
                                />
                                <span className="text-gray-400">-</span>
                                <input
                                  type="time"
                                  value={editEndTime}
                                  onChange={(e) => setEditEndTime(e.target.value)}
                                  className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                                  disabled={saving}
                                />
                              </div>
                              {hasOverlap && (
                                <div className="flex items-center gap-2 mb-3 p-2 bg-red-900/30 border border-red-500 rounded text-red-400 text-sm">
                                  <AlertTriangle size={16} />
                                  <span>Overlaps with existing hours</span>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSaveEdit(hour.lane_operating_hours_id)}
                                  disabled={saving || hasOverlap}
                                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                    hasOverlap 
                                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                                      : 'bg-green-600 hover:bg-green-700 text-white'
                                  }`}
                                >
                                  <Check size={18} />
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={saving}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                                >
                                  <X size={18} />
                                  Cancel
                                </button>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div 
                            key={hour.lane_operating_hours_id} 
                            className={`${laneTypeColor.bg} border ${laneTypeColor.border} rounded-lg p-4 ${canUpdate ? 'cursor-pointer' : ''}`}
                            onClick={canUpdate ? () => handleStartEdit(hour) : undefined}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className={`${laneTypeColor.text} font-mono text-lg font-semibold flex items-center gap-2`}>
                                <Clock size={18} className="opacity-60" />
                                {formatTime(hour.start_time)} - {formatTime(hour.end_time)}
                              </div>
                              {canUpdate && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(hour.lane_operating_hours_id);
                                  }}
                                  className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={18} className="text-white" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Add button if there are already some slots */}
                  {canUpdate && hours.length > 0 && (
                    <button
                      onClick={() => onAdd({
                        checkpoint_id: checkpoint.checkpoint_id,
                        checkpoint_name: checkpoint.checkpoint_name,
                        day_of_week: selectedDay,
                        lanetype_id: laneType.lane_type_id
                      })}
                      className="w-full flex items-center justify-center gap-2 mt-3 px-4 py-2 text-sm text-gray-400 hover:text-blue-400 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
                    >
                      <Plus size={16} />
                      Add Another Slot
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
